// Inline links inside body copy, expressed as DATA.
//
// The blog body is a list of serialisable blocks (see blogPosts.js) and has to
// stay that way — no JSX, no components — so these rows can import straight
// into `cms_page` and the SEO team edits them without a developer. That rules
// out the obvious approach of letting an author drop a <Link> into a paragraph.
//
// So links are markup inside the text itself, in the one syntax every writer
// already knows:
//
//     "…this is the [partial-period tax](/blog/form-2290-weight-categories)."
//
// It works anywhere text is rendered — paragraphs, list items, callouts, table
// cells, FAQ answers — without adding a block type per context, and it survives
// the trip through a database column unchanged.
//
// WHY THIS EXISTS AT ALL: before this, `blogPosts.js` had no link block of any
// kind. Across 95 paragraphs there were exactly zero contextual internal links —
// the only internal linking on an article was the "Related guides" card grid at
// the very bottom. Contextual in-body links are the ones that actually pass
// relevance, and they were the single largest SEO gap on the site.

// [visible text](/path). The label may not contain a "]" or a newline; the href
// may not contain whitespace or ")". Anything that doesn't match is left alone,
// so ordinary square brackets in prose are safe.
const LINK_PATTERN = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;

/** Same-site paths only — these render as a react-router <Link>. */
export const isInternalHref = (href) => href.startsWith("/") && !href.startsWith("//");

/**
 * Off-site links. https only, deliberately: the IRS pages we cite are all
 * https, and allowing arbitrary schemes here is how "javascript:" ends up in an
 * href. Protocol-relative "//host" is excluded by isInternalHref above.
 */
export const isExternalHref = (href) => href.startsWith("https://");

/**
 * Splits text into an array of { type: "text" | "link" } parts.
 *
 * An href that is neither internal nor external is NOT silently dropped and NOT
 * turned into a link — the raw "[label](href)" is emitted as literal text, so a
 * bad target is visible on the page instead of failing open into an anchor
 * nobody vetted. scripts/check-internal-links.mjs fails the build on one anyway.
 */
export const parseRichText = (text) => {
  const source = String(text ?? "");
  const parts = [];
  let cursor = 0;

  for (const match of source.matchAll(LINK_PATTERN)) {
    const [raw, label, href] = match;

    if (match.index > cursor) {
      parts.push({ type: "text", text: source.slice(cursor, match.index) });
    }

    if (isInternalHref(href) || isExternalHref(href)) {
      parts.push({ type: "link", text: label, href });
    } else {
      parts.push({ type: "text", text: raw });
    }

    cursor = match.index + raw.length;
  }

  if (cursor < source.length) parts.push({ type: "text", text: source.slice(cursor) });
  return parts;
};

/**
 * The same text with link syntax resolved to its visible label.
 *
 * Required anywhere the string leaves the DOM and goes somewhere that can't
 * render markup — JSON-LD, meta descriptions, og:description. Emitting a raw
 * "[label](/path)" into schema.org markup is an invalid-structured-data bug,
 * and it is the kind that ships silently because the page itself looks right.
 */
export const plainText = (text) => parseRichText(text).map((p) => p.text).join("");

/** Every link target in a string. Used by the build-time link checker. */
export const linkTargets = (text) =>
  parseRichText(text)
    .filter((p) => p.type === "link")
    .map((p) => p.href);
